import React from 'react';
import { IMsalContext, useMsal } from "@azure/msal-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Constants, PropertyConstants } from "../../../Models/Constants";
import { IDataSample } from "../../../Models/IDataSample";
import { IMultiSectionUnitConfiguration, IUnitConfiguration } from "../../../Models/IUnitConfiguration";
import { ApiService } from "../../../Services/ApiService";
import { deCapitalizeFirstLetter} from "../../../Services/Global";
import { SourceType } from "../../../Utils/SourceType";
import { ConditionalTextFieldInputs } from '../../Atom/ConditionalTextFields/ConditionalTextFieldInputs';
import { ConditionalTextFieldOutputs } from '../../Atom/ConditionalTextFields/ConditionalTextFieldOutputs';
import { PheTypePlatePackInputForm } from '../SME/DesignSessionReview/PheTypePlatePackInputForm';
import { SMEDesignGuidelines } from '../../Atom/SMEDesignGuidelines/SMEDesignGuidelines';
import { IPlatePackConfiguration } from '../../../Models/IPlatePackConfiguration';
import { checkPlatePackMultiSectionValidity } from '../../../Utils/PlatePackConfgiurationHelper';
import { MultiSectionFlowchart } from '../../Atom/MultiSectionVisualization/MultiSectionFlowchart';
import logger from '../../../Services/LoggingService';
import { 
    createProductInfoStyles, 
    createProductChipStyles, 
    createTargetChipStyles 
} from '../../../theme/formStyles';
import { 
    createFlexStyles, 
    createColumnStyles, 
    createHeaderStyles, 
    createSpacingStyles, 
    createStatusStyles 
} from '../../../theme/layoutStyles';

// Material UI imports
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
    Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import CommentIcon from '@mui/icons-material/Comment';

/**
 * ModelDataSampleForm component - Material UI version
 * Model data sample form component for displaying the model data sample form 
 */
export const ModelDataSampleForm: React.FC<{}> = () => {
    const theme = useTheme();
    const flexStyles = createFlexStyles(theme);
    const columnStyles = createColumnStyles(theme);
    const headerStyles = createHeaderStyles(theme);
    const spacingStyles = createSpacingStyles(theme);
    const statusStyles = createStatusStyles(theme);
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as any;
    const params = useParams();

    const ctx: IMsalContext = useMsal();
    const apiService = useMemo(() => new ApiService(ctx), [ctx]);

    const [unitConfiguration, setUnitConfiguration] = useState<IUnitConfiguration>(state.unitConfiguration as IUnitConfiguration);
    const [multiSectionConfig, setMultiSectionConfig] = useState<IMultiSectionUnitConfiguration[]>([]);
    const [activeIndex, setActiveIndex] = useState(1);
    const [flowChartSelectableProperties, setFlowChartSelectableProperties] = useState<string[]>();
    const activeSection = multiSectionConfig.find(x => x.sectionNumber === activeIndex) ?? {} as IMultiSectionUnitConfiguration;
    const [dataSamples, setDataSamples] = useState<IDataSample[]>([]);
    const [pheTypes, setPheTypes] = useState<{ key: string, text: string }[]>([]);

    const [pheType, setPheType] = useState('');
    const [platePackConfigs, setPlatePackConfigs] = useState<IPlatePackConfiguration[]>([]);


    const [isLoading, setIsLoading] = useState(true);
    const [copyMessage, setCopyMessage] = useState('');
    const [formIsValid, setFormIsValid] = useState(false);


    const currentSectionIndex = activeIndex - 1;

    // Styles matching DesignSessionReviewForm patterns
    const styles = {
        // Main layout styles (using centralized)
        pageContainer: spacingStyles.pageContainer,
        
        // Header section styles (using centralized)
        header: headerStyles.pageHeader,
        headerLeft: flexStyles.flexCenterGap2,
        backButton: {
            height: 'fit-content'
        },
        submitButton: {
            marginLeft: theme.spacing(2)
        },

        // Main content layout
        mainContent: {
            display: 'flex',
            flexDirection: {
                xs: 'column',
                lg: 'row'
            },
            gap: theme.spacing(2),
            width: '100%'
        },

        // Column containers (using centralized with overrides)
        leftColumnContainer: {
            ...columnStyles.columnContainer,
            padding: 0, // Override for this specific case
            flex: '1 1 33.33%',
            maxWidth: '33.33%'
        },
        configContainer: {
            ...columnStyles.configColumn,
            flex: '1 1 33.33%'
        },
        smeContainer: {
            ...columnStyles.smeColumn,
            flex: '1 1 33.33%',
            maxWidth: '33.33%'
        },
        guidelinesContainer: {
            ...columnStyles.primaryColumn,
            flex: '1 1 33.33%',
            maxWidth: '33.33%',
            '& > div': {
                margin: 0,
                width: '100%'
            }
        },

        // Product information styles (using centralized form styles)
        productInfo: createProductInfoStyles(theme),
        productChip: createProductChipStyles(theme),
        targetChip: createTargetChipStyles(theme),

        // Section navigation styles (using centralized)
        sectionNavigation: headerStyles.sectionNavigation,
        flowchartContainer: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: theme.spacing(1),
            width: '100%',
            overflow: 'auto'
        },

        // Column headers (using centralized)
        columnHeader: headerStyles.sectionHeader,
        columnTitle: {
            minWidth: '100px',
            marginBottom: theme.spacing(1)
        },

        // Form section styles
        formSection: {
            marginBottom: theme.spacing(3),
            padding: 0,
            borderRadius: theme.shape.borderRadius
        },
        sectionTitle: {
            fontWeight: 500,
            marginBottom: theme.spacing(1.5),
            color: theme.palette.text.primary
        },
        fieldLabel: {
            ...flexStyles.flexCenter,
            fontWeight: 500
        },
        requiredMark: {
            color: theme.palette.error.main,
            marginLeft: theme.spacing(0.5)
        },
        inputGroup: {
            marginBottom: theme.spacing(2.5)
        },
        commentField: {
            '& .MuiInputBase-root': {
                minHeight: 100
            }
        },

        // Sampling info styles
        samplingInfo: {
            padding: theme.spacing(2),
            backgroundColor: theme.palette.grey[50],
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.divider}`,
            marginBottom: theme.spacing(2)
        },
        copyButton: {
            ...flexStyles.flexCenterGap1,
            marginTop: theme.spacing(1)
        },
        copySuccess: {
            color: theme.palette.success.main
        },

        // Modeler comment styles
        modelerComment: {
            padding: theme.spacing(2),
            backgroundColor: theme.palette.info.light + '20',
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.info.main}`,
            marginBottom: theme.spacing(2)
        },
        commentHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            marginBottom: theme.spacing(1)
        },

        // Loading and status styles (using centralized)
        loadingContainer: statusStyles.loadingContainer,
        loadingText: {
            marginLeft: theme.spacing(2)
        },

        // Utility styles (now available from centralized imports)
        fullWidth: spacingStyles.fullWidth,
        marginBottom1: spacingStyles.marginBottom1,
        marginBottom2: spacingStyles.marginBottom2
    };

    /**
     * Get data samples from the API and set the state
     */
    async function getDataSamplesAsync() {
        const response = await apiService.getAsync(`settings/unitconfigurations/sections`, unitConfiguration);
        if (!response.ok) {
            throw new Error(`Could not get sections. ${response.status} ${response.statusText}`);
        }

        const sections: IMultiSectionUnitConfiguration[] = await response.json();
        const orderedSections = sections.sort((a, b) => { return b.sectionNumber! - a.sectionNumber! });
        setMultiSectionConfig(orderedSections);
        let selectableProps = orderedSections[0].inputs?.concat(orderedSections[0].outputs ?? []);
        setFlowChartSelectableProperties(selectableProps);
        setPlatePackConfigs(new Array(sections.length).fill({} as IPlatePackConfiguration));

        let currentDataSamples: IDataSample[] = [];

        logger.debug("Unit configuration loaded", {
            isMultiSection: sections[0]?.isMultiSection,
            sectionsCount: sections.length
        }, {
            component: 'ModelDataSampleForm',
            method: 'getDataSamplesAsync'
        });
        if (sections[0].isMultiSection === true) {
            const dataSampleResponse = await apiService.getAsync(`modeling/datasamples/sections/${state.dataSample.sectionId}`);
            if (!dataSampleResponse.ok) {
                throw new Error(`Could not get data samples. ${dataSampleResponse.status} ${dataSampleResponse.statusText}`);
            }

            currentDataSamples = await dataSampleResponse.json();
        }
        else {
            const dsResponse = await apiService.getAsync(`modeling/datasamples/${state.dataSample.id}`);
            if (!dsResponse.ok) {
                throw new Error(`Could not get data sample. ${dsResponse.status} ${dsResponse.statusText}`);
            }

            const ds: IDataSample = await dsResponse.json();
            currentDataSamples = [ds];
        }

        logger.debug("Data samples loaded", {
            samplesCount: currentDataSamples.length,
            isMultiSection: sections[0]?.isMultiSection
        }, {
            component: 'ModelDataSampleForm',
            method: 'getDataSamplesAsync'
        });

        let currentConfigs = platePackConfigs;
        // set the configs for the plate pack configurations based on the data samples retrieved
        for (let i = 0; i < currentDataSamples.length; i++) {
            currentConfigs[i] = {
                cold_1_channels_per_pass_type_1: currentDataSamples[i].cold_1_channels_per_pass_type_1,
                cold_1_channels_per_pass_type_2: currentDataSamples[i].cold_1_channels_per_pass_type_2,
                cold_1_passes: currentDataSamples[i].cold_1_passes,
                cold_2_channels_per_pass: currentDataSamples[i].cold_2_channels_per_pass,
                cold_2_passes: currentDataSamples[i].cold_2_passes,
                cold_plate_pattern_type_1: currentDataSamples[i].cold_plate_pattern_type_1,
                cold_plate_pattern_type_2: currentDataSamples[i].cold_plate_pattern_type_2,
                hot_1_channels_per_pass_type_1: currentDataSamples[i].hot_1_channels_per_pass_type_1,
                hot_1_channels_per_pass_type_2: currentDataSamples[i].hot_1_channels_per_pass_type_2,
                hot_1_passes: currentDataSamples[i].hot_1_passes,
                hot_2_channels_per_pass: currentDataSamples[i].hot_2_channels_per_pass,
                hot_2_passes: currentDataSamples[i].hot_2_passes,
                hot_plate_pattern_type_1: currentDataSamples[i].hot_plate_pattern_type_1,
                hot_plate_pattern_type_2: currentDataSamples[i].hot_plate_pattern_type_2,
                type: currentDataSamples[i].hot_1_channels_per_pass_type_2 !== undefined && currentDataSamples[i].hot_1_channels_per_pass_type_2 !== null ?
                    Constants.VaryingTheta :
                    currentDataSamples[i].hot_2_channels_per_pass !== undefined && currentDataSamples[i].hot_2_channels_per_pass !== null ?
                        Constants.VaryingChannelsPasses : Constants.BasicPass
            }
        }

        setPlatePackConfigs(currentConfigs);
        setDataSamples(currentDataSamples);
        setPheType(currentDataSamples[0].phePlateType ?? '');
        setIsLoading(false);
    }

    useEffect(() => {
        if (state.dataSample === undefined) {
            getDataSamplesAsync();
        };

        // get the unit's user inputs to filter them out of the form for SME review
        async function getUnitConfiguration() {
            const response = await apiService.getAsync(`settings/unitconfigurations/sections`, unitConfiguration);
            if (!response.ok) {
                throw new Error(`Could not get unit configuration. ${response.status} ${response.statusText}`);
            }

            const result: IUnitConfiguration[] = await response.json();
            setUnitConfiguration(result[0]);
            await getPHETypes(result[0]);
            await getDataSamplesAsync();
        }

        // get the available PHE types for the dropdown list in the form 
        async function getPHETypes(unitConfig: IUnitConfiguration) {
            let pheTypes: { key: string, text: string }[] = [];
            const response = await apiService.getAsync(`settings/phetypes/available`, unitConfig);

            if (!response.ok) {
                throw new Error(`Could not get PHE types. ${response.status} ${response.statusText}`);
            }
            const types: any[] = await response.json();
            types.sort((a, b) => a.text.localeCompare(b.text));
            for (let i = 0; i < types.length; i++) {
                pheTypes.push({
                    key: types[i].key,
                    text: types[i].text
                })
            }

            setPheTypes(pheTypes);
        }

        getUnitConfiguration();
    }, [state]);

    // for edit mode form validation on load
    useEffect(() => {
        validateForm(true);
    }, [platePackConfigs, dataSamples]);

    const getSMEGiudelineId = (id?: bigint) => {

        let currentDataSamples = dataSamples;
        currentDataSamples.forEach((session: any, index) => {
            session['smeDesignGuideLineId'] = id;
        });
    }

    /**
     * Validate the form 
     */
    const validateForm = (input?: boolean) => {
        if ((input === true || input === undefined) &&
            checkMultiSectionValidity() === true &&
            (document.forms[0] === undefined || (document.forms[0] !== undefined && document.forms[0].checkValidity() === true))) {
            setFormIsValid(true);
        }
        else {
            setFormIsValid(false);
        }
    }

    function checkMultiSectionValidity() {
        let isValid = true;
        dataSampleLoop: for (let dataSample of dataSamples) {
            if (dataSample.phePlateType == null || dataSample.phePlateType == undefined || dataSample.phePlateType === '') {
                isValid = false;
                break dataSampleLoop;
            }
        }

        if (isValid === true) {
            isValid = checkPlatePackMultiSectionValidity(platePackConfigs);
        }
        return isValid;
    }

    /**
     * Handle change event for the form 
     */
    const handleChange = (property: string, value: any) => {
        let current = dataSamples[currentSectionIndex];

        if (value.currentTarget !== undefined) {
            value = value.currentTarget.value;
        }

        //assign same property to all datasamples if it is phe plate type and SME comment
        let currentSamples = dataSamples;
        if (property === PropertyConstants.PHEPlateType || property === PropertyConstants.SMEComment) {
            currentSamples.forEach((dataSample: any, index) => {
                dataSample[`${deCapitalizeFirstLetter(property)}`] = value;
            });
        }
        else {
            current = { ...current, [`${deCapitalizeFirstLetter(property)}`]: value };
        }

        currentSamples[currentSectionIndex] = current;
        setDataSamples([...currentSamples]);
        validateForm();
    }

    const handlePlatePackConfigurationChange = (config: IPlatePackConfiguration) => {
        let currentConfigs = platePackConfigs;
        currentConfigs[currentSectionIndex] = config;
        setPlatePackConfigs([...currentConfigs]);
        validateForm();
    }

    /**
     * Submit the form 
     */
    async function submitForm(source: SourceType) {
        setIsLoading(true);

        for (let i = 0; i < multiSectionConfig.length; i++) {
            // phePlateType is universal for all sections
            let body: any = { ...dataSamples[i], ...platePackConfigs[i], phePlateType: pheType }

            const response = await apiService.putAsync(body, `modeling/positions/${dataSamples[i]?.id}`);
            if (!response.ok) {
                throw Error(`Could not send response. ${response.status} ${response.statusText}`);
            }
        }

        setIsLoading(false);
        navigate(-1);
    }

    function handleCopyId(id: string) {
        navigator.clipboard.writeText(id);
        setCopyMessage('Copied to clipboard!');
        setTimeout(() => {
            setCopyMessage('');
        }, 1000);
    }

    // Render the SME container
    const renderSMEContainer = () => (
        <Paper sx={styles.smeContainer} elevation={1}>
            <Box component="form" onSubmit={(e: any) => { e.preventDefault(); submitForm(SourceType.SME); }}>
                <Box sx={styles.columnHeader}>
                    <Typography variant="h3" fontWeight="bold" sx={styles.columnTitle}>SME</Typography>
                </Box>
                
                {platePackConfigs && (
                    <Box sx={styles.formSection}>
                        <Typography variant="subtitle1" sx={styles.sectionTitle}>
                            {multiSectionConfig.length > 1 ? `Plate pack configuration section ${currentSectionIndex + 1}` : 'Plate pack configuration'}
                        </Typography>
                        
                        <Box sx={styles.inputGroup}>
                            <PheTypePlatePackInputForm
                                isReadOnly={false}
                                existingConfig={platePackConfigs[currentSectionIndex]}
                                pheTypes={pheTypes!}
                                setConfig={(c) => handlePlatePackConfigurationChange(c)}
                                setPheType={(phe) => { setPheType(phe); handleChange(PropertyConstants.PHEPlateType, phe) }}
                                existingPheType={pheType ?? ''}
                                shouldValidateFormInputs={true}
                                validateForm={validateForm}
                                label=""
                            />
                        </Box>
                    </Box>
                )}

                {dataSamples !== undefined && dataSamples.length > 0 && dataSamples[currentSectionIndex] && (
                    <Box sx={styles.formSection}>
                        <Typography variant="subtitle1" sx={styles.sectionTitle}>Process outputs</Typography>
                        <ConditionalTextFieldOutputs
                            disabled={false}
                            handleChange={(e: any) => handleChange(e.currentTarget.name, e.currentTarget.value)}
                            unitConfiguration={activeSection!}
                            hotMediaFlowrate={dataSamples[currentSectionIndex].hotMediaFlowrate ?? ''}
                            hotMediaTempIn={dataSamples[currentSectionIndex].hotMediaTempIn ?? ''}
                            hotMediaTempOut={dataSamples[currentSectionIndex].hotMediaTempOut ?? ''}
                            hotMediaPressureDrop={dataSamples[currentSectionIndex].hotMediaPressureDrop ?? ''}
                            coldMediaFlowrate={dataSamples[currentSectionIndex].coldMediaFlowrate ?? ''}
                            coldMediaTempIn={dataSamples[currentSectionIndex].coldMediaTempIn ?? ''}
                            coldMediaTempOut={dataSamples[currentSectionIndex].coldMediaTempOut ?? ''}
                            coldMediaPressureDrop={dataSamples[currentSectionIndex].coldMediaPressureDrop ?? ''}
                            hotMediaWallTemp={dataSamples[currentSectionIndex].hotMediaWallTemp ?? ''}
                            coldMediaWallTemp={dataSamples[currentSectionIndex].coldMediaWallTemp ?? ''}
                            addStyling={true}
                        />
                    </Box>
                )}
                
                <Box sx={styles.formSection}>
                    <Typography variant="subtitle1" sx={styles.sectionTitle}>Comment</Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={dataSamples[currentSectionIndex]?.smeComment ?? ''}
                        onChange={(e) => handleChange(PropertyConstants.SMEComment, e.currentTarget.value)}
                        sx={styles.commentField}
                        placeholder="Add your comments here..."
                    />
                </Box>
            </Box>
        </Paper>
    );

    return (
        <Box sx={styles.pageContainer}>
            <Box sx={styles.header}>
                <Box sx={styles.headerLeft}>
                    <Button 
                        variant="outlined" 
                        startIcon={<ArrowBackIcon />} 
                        sx={styles.backButton}
                        onClick={() => navigate(-1)}
                    >
                        Back
                    </Button>
                    
                    {dataSamples?.length > 0 && (
                        <Box sx={flexStyles.flexCenter}>
                            <Typography variant="body1" component="div">
                                {`${dataSamples[0].industry} / ${dataSamples[0].subIndustry} / ${dataSamples[0].application} / ${dataSamples[0].subApplication} / `}
                                <Typography component="span" fontWeight="bold" variant="body1">
                                    {dataSamples[0].alfaLavalPosition}
                                </Typography>
                            </Typography>
                        </Box>
                    )}
                </Box>
                
                <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<CheckIcon />}
                    onClick={() => submitForm(SourceType.SME)}
                    disabled={isLoading || !formIsValid}
                    sx={styles.submitButton}
                >
                    {multiSectionConfig.length > 1 ? 'Submit all sections' : 'Submit'}
                </Button>
            </Box>
            
            {isLoading && (
                <Box sx={styles.loadingContainer}>
                    <CircularProgress size={40} />
                    <Typography variant="body1" sx={styles.loadingText}>Loading data sample...</Typography>
                </Box>
            )}
            
            {!isLoading && dataSamples?.length > 0 && (
                <Box sx={styles.mainContent}>
                    {/* Left column - Configuration details */}
                    <Stack spacing={2} sx={styles.leftColumnContainer}>
                        <Box sx={styles.productInfo}>
                            <Box sx={{...flexStyles.flexCenter, ...styles.marginBottom1}}>
                                <Typography variant="body1" component="span">
                                    Product: 
                                </Typography>
                                <Chip 
                                    label={activeSection?.hotMediaType}
                                    variant="outlined"
                                    size="small"
                                    sx={styles.productChip}
                                />
                            </Box>
                            
                            <Typography variant="body1" sx={styles.marginBottom1}>
                                Process stage: {activeSection?.processStage}
                            </Typography>
                            
                            {dataSamples[currentSectionIndex] !== undefined && 
                             activeSection?.unitOperationTargetName !== undefined && (
                                <Box sx={flexStyles.flexCenterBetween}>
                                    <Typography variant="body1" component="div">
                                        Unit operation target: {activeSection.unitOperationTargetName} 
                                    </Typography>
                                    
                                    <Chip
                                        label={`${dataSamples[currentSectionIndex].unitOperationTargetValue?.toString()} ${activeSection.unitOperationTargetValueUnit || ''}`}
                                        size="small"
                                        sx={styles.targetChip}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* Modeler Comment */}
                        {dataSamples[currentSectionIndex] !== undefined && 
                         dataSamples[currentSectionIndex].modelerComment !== '' && 
                         dataSamples[currentSectionIndex].modelerComment !== undefined && 
                         dataSamples[currentSectionIndex].modelerComment !== null && (
                            <Box sx={styles.modelerComment}>
                                <Box sx={styles.commentHeader}>
                                    <Typography variant="subtitle2" fontWeight="medium">
                                        Modeler comment
                                    </Typography>
                                    <CommentIcon fontSize="small" />
                                </Box>
                                <Typography variant="body2">
                                    {dataSamples[currentSectionIndex].modelerComment}
                                </Typography>
                            </Box>
                        )}
                        
                        <Paper sx={styles.configContainer} elevation={0}>
                            {multiSectionConfig?.length > 1 && (
                                <Box sx={styles.marginBottom2}>
                                    <Box sx={styles.sectionNavigation}>
                                        <IconButton 
                                            onClick={() => setActiveIndex(activeIndex === dataSamples.length ? dataSamples.length : activeIndex + 1)}
                                        >
                                            <ChevronLeftIcon />
                                        </IconButton>
                                        
                                        <Typography variant="subtitle1" fontWeight="medium">
                                            Section {activeIndex}
                                        </Typography>
                                        
                                        <IconButton 
                                            onClick={() => setActiveIndex(activeIndex === 1 ? 1 : activeIndex - 1)}
                                        >
                                            <ChevronRightIcon />
                                        </IconButton>
                                    </Box>
                                    
                                    <Box sx={styles.flowchartContainer}>
                                        <MultiSectionFlowchart
                                            selectableProperties={flowChartSelectableProperties ?? []}
                                            sectionCount={multiSectionConfig.length}
                                            unitInputConfiguration={multiSectionConfig[0]}
                                            scaling={1 / multiSectionConfig.length * 1.8}
                                        />
                                    </Box>
                                </Box>
                            )}
                            
                            <Box sx={styles.marginBottom2}>
                                <Typography variant="subtitle1" fontWeight="medium" sx={styles.marginBottom1}>Inputs</Typography>
                                {dataSamples[currentSectionIndex] !== undefined && (
                                    <ConditionalTextFieldInputs
                                        disabled={false}
                                        unitConfiguration={activeSection!}
                                        originPage={Constants.ModelDataSampleForm}
                                        propertySpecs={[]}
                                        coldMediaFlowrate={dataSamples[currentSectionIndex].coldMediaFlowrate?.toString()}
                                        coldMediaTempIn={dataSamples[currentSectionIndex].coldMediaTempIn?.toString()}
                                        coldMediaTempOut={dataSamples[currentSectionIndex].coldMediaTempOut?.toString()}
                                        coldMediaPressureDrop={dataSamples[currentSectionIndex].coldMediaPressureDrop?.toString()}
                                        hotMediaFlowrate={dataSamples[currentSectionIndex].hotMediaFlowrate?.toString()}
                                        hotMediaTempIn={dataSamples[currentSectionIndex].hotMediaTempIn?.toString()}
                                        hotMediaTempOut={dataSamples[currentSectionIndex].hotMediaTempOut?.toString()}
                                        hotMediaPressureDrop={dataSamples[currentSectionIndex].hotMediaPressureDrop?.toString()}
                                        addStyling={true}
                                        handleChange={undefined} 
                                    />
                                )}
                            </Box>

                            {/* Sampling ID section */}
                            <Box sx={styles.samplingInfo}>
                                <Typography variant="subtitle2" fontWeight="medium" sx={styles.marginBottom1}>
                                    Sampling Id
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={styles.marginBottom1}>
                                    {params.samplingId}
                                </Typography>
                                
                                <Box sx={styles.copyButton}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={copyMessage ? <CheckCircleIcon /> : <ContentCopyIcon />}
                                        onClick={() => handleCopyId(dataSamples[currentSectionIndex]?.id.toString())}
                                        sx={copyMessage ? styles.copySuccess : {}}
                                    >
                                        {copyMessage ? 'Id copied' : `Id: ${dataSamples[currentSectionIndex]?.id}`}
                                    </Button>
                                    
                                    {copyMessage ? (
                                        <CheckCircleIcon sx={styles.copySuccess} />
                                    ) : (
                                        <Tooltip title="To reference this version in another dataset, use this ID in the ReferenceId field in your csv file.">
                                            <InfoIcon color="primary" sx={{ cursor: 'pointer' }} />
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    </Stack>
                    
                    {/* Middle column - SME form */}
                    {renderSMEContainer()}
                    
                    {/* Right column - SME design guidelines */}
                    {unitConfiguration !== undefined && (
                        <Box sx={styles.guidelinesContainer}>
                            <SMEDesignGuidelines
                                isReadOnly={false}
                                setDesignGuidelineId={(e) => getSMEGiudelineId(e)}
                                dataSample={dataSamples[0]}
                                unitConfiguration={unitConfiguration} 
                            />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}; 